import { CartEntrySummary } from "./CartEntrySummary";
import { CartEntryDetails } from "./CartEntryDetails";
import { Card } from "~/common/mui";
import { CheckoutProductLine } from "./CheckoutProductLine";
import { CartProductLine } from "./CartProductLine";
import { useEffect, useRef } from "react";
import type { CartEntryRowViewModel, ProductLineViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { useLineItemActions } from "~/domains/cart/hooks";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

interface CartEntryProps {
  entry: CartEntryRowViewModel;
  editMode: boolean;
  onRender: (element: Element) => void;
  onDeleteProduct: () => void;
  onDeleteLine: (line: ProductLineViewModel) => void;
  onCopyLine: (packEntryId: number) => void;
}

interface EditableLineWrapperProps {
  line: ProductLineViewModel;
  mainDescriptor: ProductDescriptor;
  onDeleteLine: (line: ProductLineViewModel) => void;
}

const EditableLineWrapper = ({ line, mainDescriptor, onDeleteLine }: EditableLineWrapperProps) => {
  const product = line.descriptor ?? mainDescriptor;
  const actions = useLineItemActions({ lineItem: line.sourceLineItem, product });
  return <CartProductLine line={line} product={product} actions={actions} onDeleteLine={() => onDeleteLine(line)} />;
};

export const CartEntry = ({ entry, editMode, onRender, onDeleteProduct, onDeleteLine, onCopyLine }: CartEntryProps) => {
  const mainDescriptor = entry.mainDescriptor;

  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = rootRef.current;
    if (!element || !onRender) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      onRender(element);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onRender]);

  if (!mainDescriptor) {
    return null;
  }

  const renderItem = (line: ProductLineViewModel) => {
    const product = line.descriptor ?? mainDescriptor;

    if (editMode) {
      return (
        <EditableLineWrapper key={line.rowId} line={line} mainDescriptor={mainDescriptor} onDeleteLine={onDeleteLine} />
      );
    }

    return <CheckoutProductLine key={line.rowId} line={line} product={product} />;
  };

  return (
    <Card ref={rootRef}>
      <CartEntrySummary
        product={mainDescriptor}
        totalQuantity={entry.summary.totalQuantity}
        lineItemCount={entry.summary.lineItemCount}
        subtotal={entry.summary.subtotal}
        hasTariffSurcharge={entry.summary.hasTariffSurcharge}
        hasIncludedSurcharge={entry.summary.hasIncludedSurcharge}
        canDeleteProduct={entry.summary.canDeleteProduct}
        editMode={editMode}
        onDeleteProduct={onDeleteProduct}
      />
      <CartEntryDetails
        canCopyLine={entry.details.canCopyLine}
        copySourcePackEntryId={entry.details.copySourcePackEntryId}
        lines={entry.details.lines}
        renderItem={renderItem}
        editMode={editMode}
        initiallyExpanded={entry.details.initiallyExpanded}
        onCopyLine={onCopyLine}
      />
    </Card>
  );
};
